/**
 * Which section the sticky tab bar should highlight, given where the sections
 * currently sit on screen.
 *
 * Pure and geometry-driven on purpose. Operators add, rename, remove and
 * reorder tabs from Settings → Tour Tabs, so the rule must not depend on how
 * many tabs there are or the order they arrive in — it sorts by measured
 * position and answers from that alone.
 *
 * The rule is "the last section whose top has passed the marker", NOT "the
 * top-most section still on screen". The old code used the latter, which
 * favours the section you are scrolling AWAY from: while the previous
 * section's tail still overlaps the observed band it sits higher than the one
 * you just jumped to, so it kept winning and the underline sat one tab behind.
 */

export interface SectionPosition {
  slug: string;
  /** Viewport-relative top edge, exactly as `getBoundingClientRect().top`
   *  reports it — negative once the section has scrolled past the top. */
  top: number;
}

/** Sub-pixel slack. A section scrolled to the marker often measures 115.99 or
 *  116.01 depending on device pixel ratio; without this, landing exactly on a
 *  tab intermittently fails to select it. */
const EPSILON = 1;

/** How far off the marker a landing may be before it is corrected. Wide enough
 *  to ignore sub-pixel and device-pixel-ratio noise, far narrower than the gap
 *  between two sections. */
export const LANDING_TOLERANCE_PX = 8;

/**
 * Whether a click-scroll that has stopped should hop again to reach the marker.
 *
 * Pure, and separate from the component, for the same reason `pickActiveSection`
 * is: it is the rule, and a rule is worth being able to state and test on its
 * own. The component supplies measurements; this decides.
 *
 * Two independent brakes, because the two failure modes are opposite:
 *
 *  - `correctionsLeft` bounds the ordinary case. Below the fold this page is
 *    `content-visibility: auto` sections carrying ESTIMATED heights, so the
 *    first landing is computed against estimates and misses; each pass replaces
 *    estimates with real heights and converges. A jump from the top of the page
 *    to the FAQ band measured 1,817px out before the first correction and 94px
 *    out after it — two passes, not one.
 *  - `lastDrift` bounds the pathological one. A page being animated, or a lazy
 *    image resolving above the reader, moves under every correction. There the
 *    gap does not shrink and hopping again is a scroll fight the reader sees.
 *
 * The improvement test is "strictly closer", not "at least halved". Halving was
 * the first attempt and it was too strict: a long jump converges 1817 → 94 → 0,
 * but a shorter one can crawl, and a pass that closed 40% of the gap was being
 * treated as a page fighting back. Strictly-closer still stops dead on the
 * pathological case, and the count is what bounds the rest.
 */
export function shouldCorrectLanding({
  drift,
  correctionsLeft,
  lastDrift,
}: {
  /** Signed pixels between the section's top and the marker. */
  drift: number;
  /** Corrections still permitted. */
  correctionsLeft: number;
  /** Absolute drift the previous pass started from; `Infinity` on the first. */
  lastDrift: number;
}): boolean {
  const gap = Math.abs(drift);
  if (gap <= LANDING_TOLERANCE_PX) return false;
  if (correctionsLeft <= 0) return false;
  return gap < lastDrift;
}

export function pickActiveSection(
  positions: SectionPosition[],
  marker: number,
  atBottom = false,
): string | null {
  if (positions.length === 0) return null;

  // Sort by where things ACTUALLY are, never by the order the tabs were
  // configured in. A reorder in Settings changes the array; it does not change
  // which section your eyes are on.
  const byPosition = [...positions].sort((a, b) => a.top - b.top);

  // The final section is often too short to ever reach the marker — at the
  // bottom of the page there is no scroll left to bring it up. Without this,
  // the last tab could never be highlighted no matter how far you scrolled.
  if (atBottom) return byPosition[byPosition.length - 1]!.slug;

  const passed = byPosition.filter((p) => p.top <= marker + EPSILON);

  // Nothing has reached the marker yet — we are above the first section, so
  // the first one is what the reader is heading into.
  return (passed.length > 0 ? passed[passed.length - 1]! : byPosition[0]!).slug;
}
